import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Loads product recommendations into the cart drawer and handles adding them to the cart.
 *
 * The element is marked `data-skip-subtree-update`, so header re-renders keep the fetched content
 * and only update attributes. A change to `data-url` (the cart contents changed) triggers a reload.
 * The element hides itself through CSS while it has no items.
 */
class CartDrawerRecommendations extends HTMLElement {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver((entries, observer) => {
    if (!entries[0]?.isIntersecting) return;

    observer.disconnect();
    this.#load();
  });

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver(() => {
    this.#load();
  });

  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(() => this.#updateArrows());

  /** @type {Record<string, string>} */
  #cache = {};

  /** @type {AbortController | null} */
  #activeFetch = null;

  #loaded = false;

  /**
   * The element itself is display: none while empty, so visibility is checked on its container.
   * @returns {Element}
   */
  get #visibilityTarget() {
    return this.parentElement ?? this;
  }

  connectedCallback() {
    this.#intersectionObserver.observe(this.#visibilityTarget);
    this.#mutationObserver.observe(this, { attributes: true, attributeFilter: ['data-url'] });
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('click', this.#handleArrowClick);
    this.addEventListener('click', this.#handleItemTap);
    this.#list?.addEventListener('scroll', this.#updateArrows, { passive: true });
    if (this.#list) this.#resizeObserver.observe(this.#list);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('click', this.#handleArrowClick);
    this.removeEventListener('click', this.#handleItemTap);
    this.#list?.removeEventListener('scroll', this.#updateArrows);
    this.#resizeObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #load() {
    const { url } = this.dataset;
    if (!url) return;

    const list = this.#list;
    if (!list) return;

    // Wait for the drawer to be opened before the first load
    if (!this.#loaded && !this.#visibilityTarget.checkVisibility()) {
      this.#intersectionObserver.observe(this.#visibilityTarget);
      return;
    }

    this.#loaded = true;

    try {
      const html = await this.#fetch(url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newList = doc.querySelector('.cart-recs__list');

      list.innerHTML = newList?.innerHTML ?? '';
      list.scrollLeft = 0;
      this.#updateArrows();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Cart drawer recommendations error:', error);
      list.innerHTML = '';
    }
  }

  /** @returns {HTMLElement | null} */
  get #list() {
    return this.querySelector('.cart-recs__list');
  }

  /**
   * Scrolls the slider by one card
   * @param {MouseEvent} event
   */
  #handleArrowClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const arrow = event.target.closest('.cart-recs__arrow');
    const list = this.#list;
    const card = list?.querySelector('.cart-recs__item');
    if (!(arrow instanceof HTMLElement) || !list || !card) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    list.scrollBy({ left: Number(arrow.dataset.direction) * (card.clientWidth + gap) });
  };

  /**
   * On touch devices there's no hover, so the first tap on an image reveals the add form instead of navigating
   * @param {MouseEvent} event
   */
  #handleItemTap = (event) => {
    if (!(event.target instanceof Element) || window.matchMedia('(hover: hover)').matches) return;

    const item = event.target.closest('.cart-recs__item');

    if (item && !item.classList.contains('is-open') && event.target.closest('.cart-recs__media')) {
      event.preventDefault();
    }

    for (const openItem of this.querySelectorAll('.cart-recs__item.is-open')) {
      if (openItem !== item) openItem.classList.remove('is-open');
    }

    item?.classList.add('is-open');
  };

  #updateArrows = () => {
    const list = this.#list;
    if (!list) return;

    const [prev, next] = this.querySelectorAll('.cart-recs__arrow');
    const maxScroll = list.scrollWidth - list.clientWidth;

    if (prev instanceof HTMLButtonElement) prev.disabled = list.scrollLeft <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = list.scrollLeft >= maxScroll - 1;
  };

  /**
   * @param {string} url
   * @returns {Promise<string>}
   */
  async #fetch(url) {
    const cached = this.#cache[url];
    if (cached) return cached;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const text = await response.text();
      this.#cache[url] = text;
      return text;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-recs__form')) return;

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const formData = new FormData(form);
    formData.append('quantity', '1');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
    });
    formData.append('sections', Array.from(sectionIds).join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.message || response.description);

        this.dispatchEvent(
          new CartAddEvent({}, String(formData.get('id')), {
            source: 'cart-drawer-recommendations',
            itemCount: 1,
            variantId: String(formData.get('id')),
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (button instanceof HTMLButtonElement) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
  };
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
